/*
 * Copyright (c) 2019 Signum+ Software & Facility GmbH
 * Author: jahn@signum-plus.at
 */

import { CoreErrorKey } from '../key/core-error.key';
import { ApplicationError } from '../model/application-error.model';

export type AdapterFactory = (source: object) => ObjectAdapter;

export class ObjectAdapter implements Iterable<any> {
  private static adapterFactories: AdapterFactory[] = [
    ObjectAdapter.fromArray,
    ObjectAdapter.fromMap
  ];

  constructor(
    public readonly source: object,
    private readonly keyIteratorFactory: () => IterableIterator<string | number>,
    private readonly valueIteratorFactory: () => IterableIterator<any>,
    private readonly entryIteratorFactory: () => IterableIterator<[string | number, any]>,
    private readonly hasValue: (key: string | number) => boolean,
    private readonly getValue: (key: string | number) => any,
    private readonly setValue: (key: string | number, value: any) => void,
    private readonly deleteValue: (key: string | number) => boolean,
    private readonly getSize: () => number
  ) { }

  keys(): IterableIterator<string | number> {
    return this.keyIteratorFactory();
  }

  values(): IterableIterator<any> {
    return this.valueIteratorFactory();
  }

  entries(): IterableIterator<[string | number, any]> {
    return this.entryIteratorFactory();
  }

  [Symbol.iterator](): IterableIterator<any> {
    return this.values();
  }

  has(key: string | number): boolean {
    return this.hasValue(key);
  }

  get(key: string | number): any {
    return this.getValue(key);
  }

  set(key: string | number, value): void {
    this.setValue(key, value);
  }

  delete(key: string | number): boolean {
    return this.deleteValue(key);
  }

  get size(): number {
    return this.getSize();
  }

  static registerAdapterFactory(factory: AdapterFactory) {
    ObjectAdapter.adapterFactories.push(factory);
  }

  static from(source: object): ObjectAdapter {
    if (source === undefined || source === null) {
      throw new ApplicationError({
        key: CoreErrorKey.SOURCE_MISSING,
        arguments: { source }
      });
    }
    if (typeof source !== 'object') {
      throw new ApplicationError({
        key: CoreErrorKey.SOURCE_INVALID,
        arguments: { source }
      });
    }
    if (source instanceof ObjectAdapter) {
      return source;
    }
    for (const factory of ObjectAdapter.adapterFactories) {
      const adapter = factory(source);
      if (adapter) {
        return adapter;
      }
    }
    return ObjectAdapter.fromPlainObject(source);
  }

  private static fromArray(source: object): ObjectAdapter {
    if (!Array.isArray(source)) {
      return null;
    }
    return new ObjectAdapter(
      source,
      () => source.keys(),
      () => source.values(),
      () => source.entries(),
      (key: string | number) => {
        return ObjectAdapter.toIndex(key) < source.length;
      },
      (key: string | number) => {
        return source[ObjectAdapter.toIndex(key)];
      },
      (key: string | number, value: any) => {
        source[ObjectAdapter.toIndex(key)] = value;
      },
      (key: string | number) => {
        const index = ObjectAdapter.toIndex(key);
        if (index >= source.length) {
          return false;
        }
        source.splice(index, 1);
        return true;
      },
      () => source.length
    );
  }

  private static toIndex(key: string | number): number {
    if (key === undefined) {
      throw new ApplicationError({
        key: CoreErrorKey.KEY_MISSING,
        message: 'Key missing'
      });
    }
    const index = typeof key === 'number' ? key : parseInt(key, 10);
    if (Number.isNaN(index) || index < 0) {
      throw new ApplicationError({
        key: CoreErrorKey.KEY_INVALID,
        arguments: { key },
        message: 'Key invalid'
      });
    }
    return index;
  }

  private static fromMap(source: object) {
    if (!(source instanceof Map)) {
      return null;
    }
    return new ObjectAdapter(
      source,
      () => source.keys(),
      () => source.values(),
      () => source.entries(),
      (key: string | number) => source.has(key),
      (key: string | number) => source.get(key),
      (key: string | number, value: any) => source.set(key, value),
      (key: string | number) => source.delete(key),
      () => source.size
    );
  }

  private static fromPlainObject(source: object) {
    return new ObjectAdapter(
      source,
      () => Object.keys(source)[Symbol.iterator](),
      () => {
        // Object.values is not supported by IE 11
        const values: Array<any> = typeof Object.values === 'function'
          ? Object.values(source)
          : Object.keys(source).map((key) => source[key]);
        return values[Symbol.iterator]();
      },
      () => {
        // Object.entries is not supported by IE 11
        const entries: Array<[string | number, any]> = typeof Object.entries === 'function'
          ? Object.entries(source)
          : Object.keys(source).map((key) => [key, source[key]]);
        return entries[Symbol.iterator]();
      },
      (key: string | number) => source.hasOwnProperty(key),
      (key: string | number) => source[key],
      (key: string | number, value: any) => {
        source[key] = value;
      },
      (key: string | number) => {
        if (!(key in source)) {
          return false;
        }
        return delete source[key];
      },
      () => Object.keys(source).length
    );
  }

}
